import type { Locale } from '@govard/i18n-core';

import { VIEW_CATALOG, inheritedViaLabel, isEntityKey } from './catalog.js';
import type { ViewColumn } from './result.js';

const EN_ENTITY_LABELS: Readonly<Record<string, string>> = {
  deal: 'Deal',
  document: 'Document',
  company: 'Company',
  contact: 'Contact',
  task: 'Task',
  note: 'Note',
  meeting: 'Meeting',
  activity: 'Event',
  memory: 'Memory entry',
};

const EN_FIELD_LABELS: Readonly<Record<string, string>> = {
  id: 'Record ID',
  createdAt: 'Created',
  updatedAt: 'Updated',
  linkedTo: 'Links',
  'deal.title': 'Title',
  'deal.amount': 'Amount',
  'deal.currency': 'Currency',
  'deal.stage': 'Stage',
  'deal.source': 'Source',
  'deal.nextActivityDue': 'Next activity',
  'deal.lastActivityAt': 'Last activity',
  'deal.tags': 'Tags',
  'deal.company': 'Company',
  'deal.contact': 'Contact',
  'deal.owner': 'Owner',
  'document.title': 'Title',
  'document.type': 'Type',
  'document.status': 'Status',
  'document.amount': 'Amount',
  'document.currency': 'Currency',
  'document.body': 'Text',
  'document.company': 'Company',
  'document.contact': 'Contact',
  'document.deal': 'Deal',
  'document.createdAt': 'Record created',
  'company.name': 'Name',
  'company.domain': 'Domain',
  'company.industry': 'Industry',
  'company.website': 'Website',
  'company.phone': 'Phone',
  'company.description': 'Description',
  'company.tags': 'Tags',
  'company.ownerMember': 'Owner',
  'contact.firstName': 'First name',
  'contact.lastName': 'Last name',
  'contact.email': 'Email',
  'contact.phone': 'Phone',
  'contact.role': 'Job title',
  'contact.source': 'Source',
  'contact.company': 'Company (text)',
  'contact.companyRef': 'Company',
  'contact.tags': 'Tags',
  'contact.notes': 'Notes (text)',
  'task.title': 'Title',
  'task.description': 'Description',
  'task.status': 'Status',
  'task.priority': 'Priority',
  'task.dueAt': 'Due date',
  'task.completedAt': 'Completed',
  'task.assigneeMember': 'Assignee',
  'task.deal': 'Deal',
  'task.company': 'Company',
  'task.contact': 'Contact',
  'task.document': 'Document',
  'note.body': 'Text',
  'note.createdByMember': 'Author',
  'note.deal': 'Linked to deal',
  'note.company': 'Linked to company',
  'note.contact': 'Linked to contact',
  'note.task': 'Linked to task',
  'note.meeting': 'Linked to meeting',
  'note.document': 'Linked to document',
  'note.linkedTo': 'Links',
  'meeting.title': 'Title',
  'meeting.description': 'Description',
  'meeting.startsAt': 'Start',
  'meeting.endsAt': 'End',
  'meeting.timezone': 'Time zone',
  'meeting.location': 'Location',
  'meeting.status': 'Status',
  'meeting.ownerMember': 'Owner',
  'meeting.deal': 'Deal',
  'meeting.company': 'Company',
  'meeting.contact': 'Contact',
  'meeting.task': 'Task',
  'meeting.document': 'Document',
  'activity.kind': 'Event type',
  'activity.subject': 'Subject',
  'activity.actor': 'Actor',
  'activity.occurredAt': 'When',
  'activity.deal': 'Deal',
  'activity.contact': 'Contact',
  'activity.company': 'Company',
  'activity.task': 'Task',
  'activity.document': 'Document',
  'memory.level': 'Level',
  'memory.key': 'Key',
  'memory.valueText': 'Text',
  'memory.importance': 'Importance',
  'memory.pinned': 'Pinned',
};

const EN_ENUM_LABELS: Readonly<Record<string, string>> = {
  'deal.stage.New': 'New',
  'deal.stage.Contacted': 'Contacted',
  'deal.stage.Negotiation': 'Negotiation',
  'deal.stage.Won': 'Won',
  'deal.stage.Lost': 'Lost',
  'document.type.contract': 'Contract',
  'document.type.invoice': 'Invoice',
  'document.type.act': 'Act',
  'document.status.draft': 'Draft',
  'document.status.sent': 'Sent',
  'document.status.signed': 'Signed',
  'document.status.cancelled': 'Cancelled',
  'task.status.todo': 'To do',
  'task.status.in_progress': 'In progress',
  'task.status.blocked': 'Blocked',
  'task.status.done': 'Done',
  'task.status.cancelled': 'Cancelled',
  'task.priority.low': 'Low',
  'task.priority.medium': 'Medium',
  'task.priority.high': 'High',
  'task.priority.urgent': 'Urgent',
  'meeting.status.scheduled': 'Scheduled',
  'meeting.status.completed': 'Completed',
  'meeting.status.cancelled': 'Cancelled',
  'memory.level.l1': 'L1 — facts',
  'memory.level.l2': 'L2 — summaries',
  'memory.level.l3': 'L3 — semantic',
};

export function localizedEntityLabel(entity: string, locale: Locale): string | null {
  if (!isEntityKey(entity)) return null;
  return locale === 'en'
    ? (EN_ENTITY_LABELS[entity] ?? VIEW_CATALOG[entity].label)
    : VIEW_CATALOG[entity].label;
}

export function localizedColumnLabel(
  entity: string,
  column: Pick<ViewColumn, 'key' | 'label'>,
  locale: Locale,
): string {
  if (locale !== 'en') return column.label;
  return EN_FIELD_LABELS[`${entity}.${column.key}`] ?? EN_FIELD_LABELS[column.key] ?? column.label;
}

export function localizedEnumLabel(
  entity: string,
  column: ViewColumn,
  value: string,
  locale: Locale,
): string {
  if (locale === 'en') return EN_ENUM_LABELS[`${entity}.${column.key}.${value}`] ?? value;
  return column.options?.find((option) => option.value === value)?.label ?? value;
}

export function localizedInheritedVia(via: string, locale: Locale): string {
  if (locale !== 'en') return inheritedViaLabel(via);
  const entity = localizedEntityLabel(via, locale);
  return entity === null ? 'via a related record' : `via ${entity.toLowerCase()}`;
}
